// NEAR wallet provider — thin React shell around @hot-labs/near-connect.
// We keep the NearConnector instance in a context so any component can
// open the picker, read the connected account, sign transactions, and
// listen for connect/disconnect events.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import { NearConnector } from "@hot-labs/near-connect";
import type { UnsignedNearTx } from "@/types/dto";

// @hot-labs/near-connect doesn't ship a `Wallet` type re-export; we
// only ever touch the methods we know exist (`signAndSendTransaction*`,
// `signOut`, `getAccounts`), so type-erase the wallet handle locally
// rather than depend on an internal shape.
type NearConnectWallet = Awaited<ReturnType<InstanceType<typeof NearConnector>["wallet"]>>;

interface NearWalletState {
  /** Connected NEAR account id (e.g. `alice.testnet`), or null. */
  account: string | null;
  available: boolean;
  /** Open the @hot-labs picker. Resolves with the chosen account, or
   *  rejects if the user dismissed. */
  connect: () => Promise<string | null>;
  disconnect: () => Promise<void>;
  /** Sign + send one tx. Returns the NEAR tx hash. */
  signAndSend: (tx: UnsignedNearTx) => Promise<string>;
  /** Sign + send a batch of txs in one wallet interaction. */
  signAndSendBatch: (txs: UnsignedNearTx[]) => Promise<string[]>;
}

const NearWalletContext = createContext<NearWalletState | null>(null);

export function NearWalletProvider({
  network,
  children,
}: PropsWithChildren<{ network: "mainnet" | "testnet" }>) {
  const connector = useMemo(
    () => new NearConnector({ network }),
    [network],
  );
  const [account, setAccount] = useState<string | null>(null);
  const [walletInstance, setWalletInstance] = useState<NearConnectWallet | null>(null);

  // Hydrate the active account on mount (handles reload-while-connected).
  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const wallet = await connector.wallet();
        const accounts = await wallet.getAccounts();
        if (!active) return;
        if (accounts.length > 0 && accounts[0]) {
          setAccount(accounts[0].accountId);
          setWalletInstance(wallet);
        }
      } catch {
        // No active session — picker hasn't been opened yet. Normal.
      }
    })();

    const onSignIn = ({ accounts }: { accounts: Array<{ accountId: string }> }) => {
      const next = accounts[0]?.accountId ?? null;
      setAccount(next);
    };
    const onSignOut = () => {
      setAccount(null);
      setWalletInstance(null);
    };
    connector.on("wallet:signIn", onSignIn);
    connector.on("wallet:signOut", onSignOut);

    return () => {
      active = false;
      connector.off("wallet:signIn", onSignIn);
      connector.off("wallet:signOut", onSignOut);
    };
  }, [connector]);

  const connect = useCallback(async () => {
    const wallet = await connector.connect();
    setWalletInstance(wallet);
    const accounts = await wallet.getAccounts();
    const next = accounts[0]?.accountId;
    if (!next) {
      throw new Error(
        "NEAR wallet connected but returned no accounts — pick an account and retry.",
      );
    }
    setAccount(next);
    return next;
  }, [connector]);

  const disconnect = useCallback(async () => {
    try {
      const wallet = walletInstance ?? (await connector.wallet());
      await wallet.signOut();
    } catch {
      // Wallet already gone — fall through and clear local state.
    }
    setAccount(null);
    setWalletInstance(null);
  }, [connector, walletInstance]);

  // Resolve the active wallet handle and cache it for subsequent calls.
  // The first sign on a freshly-restored session has `walletInstance ==
  // null`; we fetch via `connector.wallet()`, then persist so we don't
  // round-trip every signing call.
  const resolveWallet = useCallback(async () => {
    if (walletInstance) return walletInstance;
    const w = await connector.wallet();
    setWalletInstance(w);
    return w;
  }, [connector, walletInstance]);

  const signAndSend = useCallback(
    async (tx: UnsignedNearTx) => {
      const wallet = await resolveWallet();
      // Our DTO carries `args: unknown` (the action's JSON payload);
      // near-connect's type-level Action narrows that to `object`.
      // The wire shape is identical — cast through.
      const result = (await wallet.signAndSendTransaction({
        receiverId: tx.receiverId,
        actions: tx.actions as Parameters<typeof wallet.signAndSendTransaction>[0]["actions"],
      })) as { transaction?: { hash?: string }; transaction_outcome?: { id?: string } };
      const hash = result?.transaction?.hash ?? result?.transaction_outcome?.id;
      if (!hash) {
        throw new Error(
          "near-connect: signAndSendTransaction returned no hash (wallet shape unrecognised)",
        );
      }
      return hash;
    },
    [resolveWallet],
  );

  const signAndSendBatch = useCallback(
    async (txs: UnsignedNearTx[]) => {
      if (txs.length === 0) return [];
      const wallet = await resolveWallet();
      type BatchTx = Parameters<typeof wallet.signAndSendTransactions>[0]["transactions"][number];
      const results = (await wallet.signAndSendTransactions({
        transactions: txs.map(
          (t): BatchTx => ({
            receiverId: t.receiverId,
            actions: t.actions as BatchTx["actions"],
          }),
        ),
      })) as Array<{ transaction?: { hash?: string }; transaction_outcome?: { id?: string } }>;
      return results.map((r, idx) => {
        const hash = r?.transaction?.hash ?? r?.transaction_outcome?.id;
        if (!hash) {
          throw new Error(`near-connect: tx #${idx + 1} returned no hash`);
        }
        return hash;
      });
    },
    [resolveWallet],
  );

  // NEAR is "available" whenever the connector exists — its picker
  // surfaces all installed wallets, so we don't need to feature-detect.
  // Memoize the value so consumers' `useCallback` deps don't churn on
  // every render of this provider.
  const value = useMemo<NearWalletState>(
    () => ({
      account,
      available: true,
      connect,
      disconnect,
      signAndSend,
      signAndSendBatch,
    }),
    [account, connect, disconnect, signAndSend, signAndSendBatch],
  );
  return <NearWalletContext.Provider value={value}>{children}</NearWalletContext.Provider>;
}

export function useNearWallet(): NearWalletState {
  const ctx = useContext(NearWalletContext);
  if (!ctx) throw new Error("useNearWallet must be inside <NearWalletProvider>");
  return ctx;
}
